import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type {
  SetPneumatici,
  SetPneumaticiCreate,
  SetPneumaticiUpdate,
  PrenotazioneCambio,
  PrenotazioneCambioCreate,
  OrdineLavoroCambio,
  OrdineLavoroCambioCreate,
  OrdineLavoroCambioUpdate,
  QrScanResult,
  TireServiceDashboard,
  ReportOccupazione,
  SetFilters,
  PrenotazioneFilters,
} from '@/types/tireService'

const TS = API_ENDPOINTS.TIRE_SERVICE

// ── SET PNEUMATICI ───────────────────────────────────────────────────────────

export const useSetPneumatici = (filters?: SetFilters) => {
  return useQuery({
    queryKey: ['set-pneumatici', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.stato) params.append('stato', filters.stato)
      if (filters?.stagione) params.append('stagione', filters.stagione)
      if (filters?.vehicle_id) params.append('vehicle_id', String(filters.vehicle_id))
      if (filters?.misura) params.append('misura', filters.misura)
      if (filters?.skip !== undefined) params.append('skip', String(filters.skip))
      if (filters?.limit !== undefined) params.append('limit', String(filters.limit))
      const response = await axiosInstance.get<SetPneumatici[]>(`${TS.SETS}?${params.toString()}`)
      return response.data
    },
  })
}

export const useVehicleSets = (vehicleId?: number) => {
  return useQuery({
    queryKey: ['vehicle-sets', vehicleId],
    queryFn: async () => {
      const response = await axiosInstance.get<SetPneumatici[]>(TS.VEHICLE_SETS(vehicleId!))
      return response.data
    },
    enabled: !!vehicleId,
  })
}

export const useCreateSet = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: SetPneumaticiCreate) => {
      const response = await axiosInstance.post<SetPneumatici>(TS.SETS, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['set-pneumatici'] })
      message.success('Set pneumatici creato')
    },
    onError: () => message.error('Errore nella creazione del set'),
  })
}

export const useUpdateSet = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SetPneumaticiUpdate }) => {
      const response = await axiosInstance.put<SetPneumatici>(TS.SET_BY_ID(id), data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['set-pneumatici'] })
      message.success('Set aggiornato')
    },
    onError: () => message.error("Errore nell'aggiornamento"),
  })
}

export const usePatchSetStato = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, stato }: { id: number; stato: string }) => {
      const response = await axiosInstance.patch<SetPneumatici>(TS.SET_STATO(id), { stato })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['set-pneumatici'] })
      message.success('Stato aggiornato')
    },
    onError: () => message.error("Errore nell'aggiornamento stato"),
  })
}

// ── PRENOTAZIONI ─────────────────────────────────────────────────────────────

export const usePrenotazioniCambio = (filters?: PrenotazioneFilters) => {
  return useQuery({
    queryKey: ['prenotazioni-cambio', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.stato) params.append('stato', filters.stato)
      if (filters?.customer_id) params.append('customer_id', String(filters.customer_id))
      if (filters?.data_da) params.append('data_da', filters.data_da)
      if (filters?.data_a) params.append('data_a', filters.data_a)
      if (filters?.skip !== undefined) params.append('skip', String(filters.skip))
      if (filters?.limit !== undefined) params.append('limit', String(filters.limit))
      const response = await axiosInstance.get<PrenotazioneCambio[]>(`${TS.PRENOTAZIONI}?${params.toString()}`)
      return response.data
    },
  })
}

export const usePrenotazioniGiorno = (data?: string) => {
  return useQuery({
    queryKey: ['prenotazioni-giorno', data],
    queryFn: async () => {
      const response = await axiosInstance.get<PrenotazioneCambio[]>(TS.PRENOTAZIONI_GIORNO(data!))
      return response.data
    },
    enabled: !!data,
  })
}

export const useCreatePrenotazione = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: PrenotazioneCambioCreate) => {
      const response = await axiosInstance.post<PrenotazioneCambio>(TS.PRENOTAZIONI, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prenotazioni-cambio'] })
      message.success('Prenotazione creata')
    },
    onError: () => message.error('Errore nella creazione della prenotazione'),
  })
}

export const usePatchPrenotazioneStato = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, stato }: { id: number; stato: string }) => {
      const response = await axiosInstance.patch<PrenotazioneCambio>(TS.PRENOTAZIONE_STATO(id), { stato })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prenotazioni-cambio'] })
      message.success('Stato prenotazione aggiornato')
    },
    onError: () => message.error("Errore nell'aggiornamento"),
  })
}

export const useDeletePrenotazione = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(TS.PRENOTAZIONE_BY_ID(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prenotazioni-cambio'] })
      message.success('Prenotazione annullata')
    },
    onError: () => message.error("Errore nell'annullamento"),
  })
}

// ── QR SCAN ──────────────────────────────────────────────────────────────────

export const useScanQr = () => {
  return useMutation({
    mutationFn: async (codice: string) => {
      const response = await axiosInstance.get<QrScanResult>(TS.QR_SCAN(codice))
      return response.data
    },
    onError: () => message.error('QR non valido o già utilizzato'),
  })
}

// ── ORDINI DI LAVORO ─────────────────────────────────────────────────────────

export const useOrdiniLavoro = () => {
  return useQuery({
    queryKey: ['ordini-lavoro-cambio'],
    queryFn: async () => {
      const response = await axiosInstance.get<OrdineLavoroCambio[]>(TS.ORDINI_LAVORO)
      return response.data
    },
  })
}

export const useCreateOrdineLavoro = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: OrdineLavoroCambioCreate) => {
      const response = await axiosInstance.post<OrdineLavoroCambio>(TS.ORDINI_LAVORO, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prenotazioni-cambio'] })
      message.success('Ordine di lavoro creato')
    },
    onError: () => message.error("Errore nella creazione dell'ordine"),
  })
}

export const useUpdateOrdineLavoro = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: OrdineLavoroCambioUpdate }) => {
      const response = await axiosInstance.put<OrdineLavoroCambio>(TS.ORDINE_LAVORO_BY_ID(id), data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prenotazioni-cambio'] })
      message.success('Ordine aggiornato')
    },
    onError: () => message.error("Errore nell'aggiornamento"),
  })
}

export const useChiudiOrdineLavoro = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await axiosInstance.post<OrdineLavoroCambio>(TS.ORDINE_CHIUDI(id))
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prenotazioni-cambio'] })
      message.success('Ordine di lavoro chiuso')
    },
    onError: () => message.error("Errore nella chiusura dell'ordine"),
  })
}

// ── AI REMINDER ──────────────────────────────────────────────────────────────

export const useGeneraReminder = () => {
  return useMutation({
    mutationFn: async (prenotazioneId: number) => {
      const response = await axiosInstance.post<{ testo: string }>(TS.REMINDER_GENERA(prenotazioneId))
      return response.data
    },
    onError: () => message.error('Errore nella generazione del reminder'),
  })
}

export const useInviaReminder = () => {
  return useMutation({
    mutationFn: async ({ id, canale, testo }: { id: number; canale: string; testo: string }) => {
      const response = await axiosInstance.post(TS.REMINDER_INVIA(id), { canale, testo })
      return response.data
    },
    onSuccess: () => {},
    onError: () => {},  // gestito nel componente
  })
}

// ── DASHBOARD ────────────────────────────────────────────────────────────────

export const useTireServiceDashboard = () => {
  return useQuery({
    queryKey: ['tire-service-dashboard'],
    queryFn: async () => {
      const response = await axiosInstance.get<TireServiceDashboard>(TS.DASHBOARD)
      return response.data
    },
    staleTime: 60 * 1000,
  })
}

export const useTireServiceReportOccupazione = () => {
  return useQuery({
    queryKey: ['tire-service-report-occupazione'],
    queryFn: async () => {
      const response = await axiosInstance.get<ReportOccupazione>(TS.REPORT_OCCUPAZIONE)
      return response.data
    },
  })
}
